/* eslint-disable max-lines-per-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-underscore-dangle */
import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, Renderer2, WritableSignal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationStart } from '@angular/router';
import { select } from '@ngrx/store';
import { kebabCase } from 'lodash-es';
import { AppSelector } from './@store/selectors/app.selectors';
import { StsSelectors } from './@store/selectors/sts.selectors';
import { MonitoringService } from './services/apm.service';
import {
  filter,
  take,
  tap,
  withLatestFrom,
  map,
  mergeAll,
  distinctUntilChanged,
} from 'rxjs/operators';
import { combineLatest, fromEvent, Observable, firstValueFrom } from 'rxjs';
import { claimValues, EPageTitle, userTypes } from './appsettings';
import { AuthService } from './services/auth/auth.service';
import { getRedirectUrl, isPrivateRoute, trimUrl } from './utils/common.utils';
import { LoggerService } from './services/logger.service';
import { EStorageKeys } from './models/storage.model';
import { Idle, DEFAULT_INTERRUPTSOURCES, AutoResume } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import OpenReplay from '@openreplay/tracker';
import { AppCommonServices } from './app-common.services';
import { InactivityService } from './services/inactivity.service';
import { createIframes } from './utils/create-iframes';
import { NavbarService } from './components/indigo-yellow-ui/services/navbar.service';
import { parseJwt } from './utils/parse-jwt';
import { ThemeNames } from './models/themes.enum';

declare const SALiveChat: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [MonitoringService],
})
export class AppComponent implements OnInit {
  private prevurl: string;
  private readonly store = this.commonService.store;
  private readonly config = this.commonService.config;
  private readonly router = this.commonService.router;
  private readonly transloco = this.commonService.transloco;

  tracker: OpenReplay;

  isLangLoading$: Observable<boolean>;
  imgs = [];
  forceMFA$ = this.store.pipe(select(StsSelectors.isMFARequired));
  useNewNav$: Observable<boolean> = this.store.pipe(select(StsSelectors.useNewNav));
  isFiTenant$ = this.store.pipe(select(StsSelectors.isFITenant));

  public readonly showMainNavbar: WritableSignal<boolean>;

  constructor(
    @Inject(DOCUMENT) private readonly document,
    private readonly renderer2: Renderer2,
    private readonly commonService: AppCommonServices,
    private readonly title: Title,
    private readonly auth: AuthService,
    private readonly loggerService: LoggerService,
    private readonly idle: Idle,
    private readonly keepalive: Keepalive,
    private readonly navbarService: NavbarService,
    private readonly inactivityService: InactivityService,
  ) {
    this.showMainNavbar = this.navbarService.showMainNavbar;
    this.setPageClass(this.renderer2);
    this.setTitle();
    this.fetchFreshToken();

    this.setupIdleTimer();
    this.setSynlabHeaderVisibility();
  }

  private setSynlabHeaderVisibility() {
    const isBluePulseTheme = this.config._envConfig.ThemeName === ThemeNames.BLUE_PULSE;
    if (isBluePulseTheme) {
      this.navbarService.showMainNavbar.set(false);
    }
  }

  private setupIdleTimer() {
    // enable idleTimer in EE and DK
    this.config.envConfig$
      .pipe(
        filter(v => !!v),
        take(1),
        withLatestFrom(this.store.pipe(select(StsSelectors.useNewUI))),
        tap(([config, isNewUI]) => {
          if (config.EnableIdleTimeOut.toLowerCase() === claimValues.TRUE) {
            const idleTimeTemp = Number(config.idleTimeOutInSeconds) || 900;
            const idleTime = Number(idleTimeTemp);

            this.loggerService.log({ idleTime });
            this.enableIdleTimer(idleTime);
          }

          if (isNewUI) {
            this.subscribeToLangChanges();
          }
          this.setCountries();
        }),
      )
      .subscribe();
  }

  private setCountries() {
    const changes$ = this.transloco.langChanges$;
    // eslint-disable-next-line no-underscore-dangle
    const authUrl: string = this.config._envConfig?.BaseAuthUrl;
    // if language changes reload country dropdowns with correct active-language http header
    changes$
      .pipe(
        tap(async () => {
          await firstValueFrom(this.config.getCountries(authUrl));
        }),
      )
      .subscribe();
  }

  signout() {
    this.auth.startSignoutMainWindow();
  }

  private preloadImages() {
    this.pload(
      'assets/images/spinner.gif',
      'assets/images/activation-link-used.png',
      'assets/images/success-icon.png',
    );
  }

  // enable OpenReplay
  private enableOpenReplay() {
    this.store
      .pipe(select(StsSelectors.getUserUserId))
      .pipe(
        withLatestFrom(
          this.config.envConfig$,
          this.store.pipe(select(StsSelectors.getEnableOpenReplay)),
          this.store.pipe(select(StsSelectors.getAppEnvironment)),
        ),
        tap(([userId, config, openReplayEnabled, env]) => {
          this.loggerService.log({ config, openReplayEnabled, userId, env });
          if (userId) {
            const { OpenReplayProjectKey, OpenReplayIngestPoint } = config;
            this.loggerService.log({ OpenReplayProjectKey, OpenReplayIngestPoint });
            if (openReplayEnabled) {
              this.tracker = new OpenReplay({
                projectKey: OpenReplayProjectKey,
                ingestPoint: OpenReplayIngestPoint,
              });
              this.tracker.start({ userID: userId, metadata: { environment: env } });
            } else if (this.tracker?.isActive) {
              this.tracker.stop();
            }
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.preloadImages();

    this.enableOpenReplay();

    setTimeout(() => {
      this.isLangLoading$ = this.store.pipe(select(AppSelector.isLanguageLoading));
    }, 0);

    this.isFiTenant$.pipe(take(1)).subscribe(isFi => {
      if (isFi) {
        this.inactivityService.setupActivityListeners();
      }
    });
    this.setupTwoWayMessaging();
  }

  private setupTwoWayMessaging() {
    this.auth._manager.events.addUserLoaded(user => {
      SALiveChat.updateToken(user.access_token);
    });

    this.auth.userInfo$
      .pipe(distinctUntilChanged((prev, curr) => !!prev === !!curr))
      .subscribe(userInfo => {
        let isUKDfp = false;
        this.store
          .select(StsSelectors.isUkDfpInstance)
          .pipe(take(1))
          .subscribe(isDfp => {
            isUKDfp = isDfp;
          });
        if (!isUKDfp) return;

        if (
          userInfo &&
          (userInfo.user_type === userTypes.SYNLAB_ACCESS_USER ||
            userInfo.user_type.includes(userTypes.SYNLAB_ACCESS_USER))
        ) {
          const oidcKey = Object.keys(sessionStorage).find(key => key.includes('oidc.user:'));
          const token = JSON.parse(sessionStorage.getItem(oidcKey));
          const { family_name: familyName, given_name: givenName } = userInfo;
          const { synlab_id: synlabId } = parseJwt(token.access_token);
          SALiveChat.initialize({
            serverUrl: 'https://stage.chat.synlabaccess.health',
            token: token.access_token,
            userName: `${givenName} ${familyName}`,
            synlabId,
          });
        } else {
          SALiveChat.remove();
        }
      });
  }

  pload(...args: string[]): void {
    for (let i = 0; i < args.length; i += 1) {
      this.imgs[i] = new Image();
      this.imgs[i].src = args[i];
    }
  }

  setTitle() {
    combineLatest([
      this.store.pipe(select(StsSelectors.isEstoniaInstance)),
      this.store.pipe(select(StsSelectors.isAppWhiteLabeled)),
    ])
      .pipe(
        take(1),
        tap(([isEETenant, isWhiteLabeled]) => {
          let title = EPageTitle.DEFAULT;
          if (isWhiteLabeled && isEETenant) {
            title = EPageTitle.ESTONIA;
          }

          this.title.setTitle(title);
        }),
      )
      .subscribe();
  }

  setPageClass(r: Renderer2) {
    this.router.events
      .pipe(
        withLatestFrom(this.store.select(StsSelectors.getWhiteLabelName)),
        tap(([event, whiteLabelName]) => {
          // set class based on tenant name
          if (whiteLabelName) {
            r.addClass(document.body, `tenant-${kebabCase(whiteLabelName)}`);
          }

          // set body class based on route path
          if (event instanceof NavigationStart) {
            if (this.prevurl) {
              r.removeClass(document.body, this.prevurl);
            }
            const currentUrlSlug = kebabCase(event.url.slice(1) || '');
            if (currentUrlSlug) {
              r.addClass(document.body, currentUrlSlug);
            }
            this.prevurl = currentUrlSlug;
          }
        }),
      )
      .subscribe();
  }

  // eslint-disable-next-line max-lines-per-function
  private fetchFreshToken() {
    const referrer = trimUrl(window.document?.referrer);
    const origin = window.location?.host;

    this.loggerService.log({ from: referrer, to: origin });
    const redirect = getRedirectUrl();
    if (referrer !== origin) {
      const routes = this.router.config;
      const privateRoute = isPrivateRoute(redirect, routes);
      this.loggerService.log(
        'Check if redirectURL is private route before calling startSigninMainWindow():',
        {
          privateRoute,
          redirect,
        },
      );

      if (privateRoute) {
        document.querySelector('body').classList.add('signin-redirecting');
        this.auth.signinRedirectTrigger$.next(true);
        if (redirect) {
          localStorage.setItem(EStorageKeys.REDIRECT_URL, redirect);
        }
      }
    } else if (redirect === '/setup-mfa' || redirect === '/register/private-complete-details') {
      localStorage.setItem(EStorageKeys.REDIRECT_URL, redirect);
    }
  }

  private enableIdleTimer(idleTimerInSeconds: number) {
    this.idle.setIdle(30); // how long can they be inactive before considered idle, in seconds
    this.idle.setTimeout(idleTimerInSeconds); // how long can they be idle before considered timed out, in seconds
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    this.idle.setAutoResume(AutoResume.idle);
    this.idle.onTimeout.subscribe(() => {
      this.loggerService.log({ onTimeout: true });
      this.auth
        .isLoggedInObs()
        .pipe(
          take(1),
          tap(isLoggedIn => {
            this.loggerService.log({ isLoggedIn });
            if (isLoggedIn) {
              this.auth.startSignoutMainWindow();
            }
          }),
        )
        .subscribe();
    });
    this.idle.onIdleStart.subscribe(() => this.loggerService.log("You've gone idle!"));
    this.idle.onTimeoutWarning.subscribe(countdown =>
      this.loggerService.log(`You will time out in ${countdown} seconds!`),
    );

    this.keepalive.interval(15);
    this.keepalive.onPing.subscribe(() => this.loggerService.log('User is idle!'));

    // right when the component initializes, start reset state and start watching
    this.reset();
  }

  reset() {
    // we'll call this method when we want to start/reset the idle process
    // reset any component state and be sure to call idle.watch()
    this.idle.watch();
  }

  cookieBotDetailToggled = false;
  cookieBotFrame: HTMLIFrameElement;

  private readonly createIframes = createIframes;

  // eslint-disable-next-line max-lines-per-function
  private subscribeToLangChanges() {
    const changes$ = this.transloco.langChanges$;
    const isCookieBotEnabled =
      // eslint-disable-next-line no-underscore-dangle
      (this.config._envConfig?.CookieBotEnabled || '').toLowerCase() === 'true';
    if (!isCookieBotEnabled) return;
    changes$
      .pipe(
        // test if consent has already been given then no need to show again.
        filter(() => !document.cookie.includes('CookieConsent')),
      )
      // eslint-disable-next-line max-lines-per-function
      .subscribe(locale => {
        if (this.cookieBotFrame?.getAttribute('data-locale') === locale) {
          return;
        }

        if (!document.querySelector('.cookiebot-iframes')) {
          // creates the iframe group for each supported locale
          const iFrameContainer: HTMLDivElement = this.document.createElement('div');
          iFrameContainer.classList.add('cookiebot-iframes');
          const availableLocales = this.transloco.getAvailableLangs() as string[];
          availableLocales.forEach(item => iFrameContainer.appendChild(this.createIframes(item)));
          this.document.querySelector('body').appendChild(iFrameContainer);
        }
        // hide all visible iframes
        document
          .querySelectorAll('.cookiebot-iframes iframe')
          .forEach(
            (el: Element) => el.classList.contains('visible') && el.classList.remove('visible'),
          );

        this.cookieBotFrame = document.querySelector(
          `.cookiebot-iframes [data-locale='${locale}']`,
        );

        if (this.cookieBotFrame) {
          this.cookieBotFrame.contentWindow.location.reload();
          const expandElementSelector =
            '#CybotCookiebotDialogBodyEdgeMoreDetailsLink, #CybotCookiebotDialogBodyLevelButtonCustomize';
          // waits for iframe to be ready, then waits for the document object to load, then wait for cookiebot to be displayed
          const onIframeReady = fromEvent(this.cookieBotFrame, 'load').pipe(
            map(() =>
              fromEvent(this.cookieBotFrame.contentWindow, 'load').pipe(
                map(() => fromEvent(this.cookieBotFrame.contentWindow, 'CookiebotOnDialogDisplay')),
              ),
            ),
          );
          const onMoreDetails = onIframeReady.pipe(
            mergeAll(),
            map(() =>
              fromEvent(
                this.cookieBotFrame.contentWindow.document.querySelectorAll(expandElementSelector),
                'click',
              ),
            ),
          );
          const onAcceptClick = onIframeReady.pipe(
            mergeAll(),
            map(() => fromEvent(this.cookieBotFrame.contentWindow, 'CookiebotOnAccept')),
          );
          const onDeclineClick = onIframeReady.pipe(
            mergeAll(),
            map(() => fromEvent(this.cookieBotFrame.contentWindow, 'CookiebotOnDecline')),
          );
          // hide the active iframe once the user accepts.
          onAcceptClick.pipe(mergeAll()).subscribe(() => {
            this.cookieBotFrame.classList.remove('visible');
          });
          // hide the active iframe once the user declines.
          onDeclineClick.pipe(mergeAll()).subscribe(() => {
            this.cookieBotFrame.classList.remove('visible');
          });
          // expand theh container height when more info is toggled
          onMoreDetails.pipe(mergeAll()).subscribe(() => {
            this.cookieBotFrame.style.height = '70vh';
            this.cookieBotDetailToggled = true;
          });
          // show only relevant cookiebot popup
          onIframeReady.pipe(mergeAll()).subscribe(() => {
            if (this.cookieBotDetailToggled)
              this.cookieBotFrame.contentWindow.document
                .getElementById('CybotCookiebotDialogBodyEdgeMoreDetailsLink')
                .click();
            this.cookieBotFrame.classList.add('visible');
          });
        }
      });
  }
}
